<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, type ComponentPublicInstance, watch } from 'vue'
import ArrowRight from '@/assets/icon/arrow-right.svg'
import { getWebHomeFeedback, type GetWebHomeFeedbackApiResponse } from '~/server/home'
import { formatRelativeTime } from '~/utils/appTime'
type FeedbackItem = {
  id: string
  reviewId: string
  company: string
  logo: string
  content: string
  author: string
  avatar: string
  country: string
  timeText: string
  title: string
  code: string
}

type FeedbackTab = {
  label: string
  type?: number
  alias?: string
}

const appStore = useAppStore()
const { locale, localePath, t } = useAppI18n()
const matomo = useMatomo()

const ITEMS_PER_PAGE = 6
const DESKTOP_ROW_SIZE = 3
const TABLET_ROW_SIZE = 2
const AUTOPLAY_INTERVAL = 3000
const props = defineProps<{
  data?: Record<string, unknown>
}>()

const feedbackPayload = ref<Record<string, unknown>>({})
const activeTab = ref('')
const activeTabType = ref<number | undefined>(undefined)
const activeTabIndex = ref(0)
const isTabletLayout = ref(false)
const tabsRef = ref<HTMLElement | null>(null)
const tabButtonRefs = ref<HTMLElement[]>([])
const feedbackTrackRef = ref<HTMLElement | null>(null)
const showRightMask = ref(false)
const trackPage = ref(1)
const isTrackTransitionEnabled = ref(true)
const isFeedbackLoading = ref(false)

let latestFeedbackRequestId = 0

const getFeedbackLanguageCode = () => appStore.LanguageCode || locale

const formatOverflowText = (text: unknown) => {
  if (typeof text !== 'string') {
    return ''
  }

  return text.length > 10 ? `${text.slice(0, 10)}...` : text
}

const normalizeFeedbackPayload = (
  payload: GetWebHomeFeedbackApiResponse | null | undefined,
): Record<string, unknown> => {
  if (!payload || typeof payload !== 'object') {
    return {}
  }

  if ('data' in payload && payload.data && typeof payload.data === 'object') {
    return payload.data as Record<string, unknown>
  }

  return payload as Record<string, unknown>
}

/** 首页反馈列表只展示完整页，不足一页的尾部数据不参与渲染。 */
const getDisplayFeedbackItems = (items: FeedbackItem[]) => {
  const displayCount = Math.floor(items.length / ITEMS_PER_PAGE) * ITEMS_PER_PAGE

  return items.slice(0, displayCount)
}

watch(
  () => props.data,
  (nextData) => {
    feedbackPayload.value = nextData && typeof nextData === 'object' ? { ...nextData } : {}
  },
  { immediate: true },
)

const tabs = computed<FeedbackTab[]>(() => {
  const payload = feedbackPayload.value
  const apiTags = payload?.tags

  if (!Array.isArray(apiTags) || apiTags.length === 0) {
    return []
  }

  const mapped = apiTags
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null
      }

      const record = item as Record<string, unknown>
      const label = typeof record.text === 'string' ? record.text : ''
      const type = typeof record.type === 'number' ? record.type : undefined
      const alias = typeof record.alias === 'string' && record.alias.trim() ? record.alias.trim() : undefined

      if (!label) {
        return null
      }

      return {
        label,
        type,
        alias,
      } satisfies FeedbackTab
    })
    .filter((item): item is FeedbackTab => item !== null)

  return mapped
})

/** 非首个反馈分类通过接口返回的 alias 跳转至对应分类页。 */
const feedbackMoreRoute = computed(() => {
  const activeTabConfig = tabs.value[activeTabIndex.value]

  if (activeTabIndex.value > 0 && activeTabConfig?.alias) {
    return `/reviews/category/${activeTabConfig.alias}`
  }

  return '/reviews'
})

const feedbackTitle = computed(() => {
  const title = feedbackPayload.value.title
  return typeof title === 'string' && title ? title : 'Feedback'
})

const feedbackItems = computed<FeedbackItem[]>(() => {
  const apiCards = feedbackPayload.value.cards

  if (!Array.isArray(apiCards) || apiCards.length === 0) {
    return []
  }

  const mappedCards = apiCards
    .map((item, index) => {
      if (!item || typeof item !== 'object') {
        return null
      }

      const record = item as Record<string, unknown>
      const user = (record.user as Record<string, unknown> | undefined) ?? {}
      const content =
        typeof record.translateContent === 'string' && record.translateContent
          ? record.translateContent
          : typeof record.content === 'string'
            ? record.content
            : ''
      const country = formatOverflowText(record.country)
      const publishTimestamp = Number(record.publishTimestamp)
      const timeText = Number.isFinite(publishTimestamp)
        ? formatRelativeTime(publishTimestamp, getFeedbackLanguageCode())
        : ''
      const reviewId =
        typeof record.feedbackId === 'string' && record.feedbackId ? record.feedbackId : `feedback-${index + 1}`
      const title = typeof record.title === 'string' && record.title ? record.title : content
      const code = typeof record.code === 'string' && record.code ? record.code : reviewId

      return {
        id: reviewId,
        reviewId,
        company: typeof record.objectName === 'string' ? record.objectName : '',
        logo: typeof record.objectLogo === 'string' ? record.objectLogo : '',
        content,
        author: formatOverflowText(user.nickname),
        avatar: typeof user.avatar === 'string' ? user.avatar : '',
        country,
        timeText,
        title,
        code,
      } satisfies FeedbackItem
    })
    .filter((item): item is FeedbackItem => item !== null)

  return getDisplayFeedbackItems(mappedCards)
})

const feedbackPages = computed<FeedbackItem[][]>(() => {
  const pages: FeedbackItem[][] = []

  for (let index = 0; index < feedbackItems.value.length; index += ITEMS_PER_PAGE) {
    pages.push(feedbackItems.value.slice(index, index + ITEMS_PER_PAGE))
  }

  return pages
})

const pageRows = computed<FeedbackItem[][][]>(() => {
  const rowSize = isTabletLayout.value ? TABLET_ROW_SIZE : DESKTOP_ROW_SIZE

  return feedbackPages.value.map((page) => {
    const rows: FeedbackItem[][] = []

    for (let index = 0; index < page.length; index += rowSize) {
      rows.push(page.slice(index, index + rowSize))
    }
    return rows
  })
})

const carouselPageRows = computed<FeedbackItem[][][]>(() => {
  if (pageRows.value.length <= 1) {
    return pageRows.value
  }

  return [pageRows.value[pageRows.value.length - 1], ...pageRows.value, pageRows.value[0]]
})

const skeletonRows = computed<number[][]>(() => {
  const rowSize = isTabletLayout.value ? TABLET_ROW_SIZE : DESKTOP_ROW_SIZE
  const rows: number[][] = []

  for (let index = 0; index < ITEMS_PER_PAGE; index += rowSize) {
    rows.push(
      Array.from({ length: rowSize }, (_, itemIndex) => {
        return index + itemIndex
      }),
    )
  }

  return rows
})

const setTabButtonRef = (el: Element | ComponentPublicInstance | null, index: number) => {
  if (!(el instanceof HTMLElement)) {
    return
  }

  tabButtonRefs.value[index] = el
}

const updateTabMask = () => {
  const tabsEl = tabsRef.value

  if (!tabsEl) {
    showRightMask.value = false
    return
  }

  const maxScrollLeft = tabsEl.scrollWidth - tabsEl.clientWidth

  showRightMask.value = maxScrollLeft > 1 && tabsEl.scrollLeft < maxScrollLeft - 1
}

const scrollTabIntoView = (index: number) => {
  const tabsEl = tabsRef.value
  const targetTab = tabButtonRefs.value[index]
  const nextTab = tabButtonRefs.value[index + 1]

  if (!tabsEl || !targetTab) {
    return
  }

  const maskWidth = 32
  const safeGap = 12
  const currentLeft = tabsEl.scrollLeft
  const tabLeft = targetTab.offsetLeft
  const tabRight = tabLeft + targetTab.offsetWidth
  // 触发点提前一位：右侧判断时把“下一个 tab”也纳入可视范围
  const triggerRight = nextTab ? nextTab.offsetLeft + nextTab.offsetWidth : tabRight
  const visibleLeft = currentLeft + safeGap
  const visibleRight = currentLeft + tabsEl.clientWidth - maskWidth - safeGap

  let nextLeft = currentLeft

  if (triggerRight > visibleRight) {
    nextLeft = triggerRight - (tabsEl.clientWidth - maskWidth - safeGap)
  } else if (tabLeft < visibleLeft) {
    nextLeft = tabLeft - safeGap
  }

  const clampedLeft = Math.max(0, Math.min(nextLeft, tabsEl.scrollWidth - tabsEl.clientWidth))

  if (Math.abs(clampedLeft - currentLeft) > 0.5) {
    tabsEl.scrollTo({
      left: clampedLeft,
      behavior: 'smooth',
    })
  }
}

const fetchFeedbackByType = async (selectedTagType?: number) => {
  if (typeof selectedTagType !== 'number') {
    return
  }

  const requestId = ++latestFeedbackRequestId
  isFeedbackLoading.value = true

  try {
    const response = await getWebHomeFeedback({
      selectedTagType,
    })

    if (requestId !== latestFeedbackRequestId) {
      return
    }

    const normalized = normalizeFeedbackPayload(response)

    feedbackPayload.value = {
      ...feedbackPayload.value,
      ...normalized,
      selectedTagType,
    }
    resetCarouselTrack()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getWebHomeFeedback failed', error)
  } finally {
    if (requestId === latestFeedbackRequestId) {
      isFeedbackLoading.value = false
    }
  }
}

const onTabClick = async (tab: FeedbackTab, index: number) => {
  if (isFeedbackLoading.value && tab.type === activeTabType.value) {
    return
  }
  matomo.trackEvent('home_feedback_tab', {
    af_type: tab.label,
  })
  activeTab.value = tab.label
  activeTabType.value = tab.type
  activeTabIndex.value = index
  stopAutoplay()
  resetCarouselTrack()
  void fetchFeedbackByType(tab.type)

  await nextTick()
  scrollTabIntoView(index)
  requestAnimationFrame(updateTabMask)
}

const onFeedbackCardClick = (card: FeedbackItem) => {
  matomo.trackEvent('home_feedback_details', {
    af_type: activeTab.value,
    af_name: card.title,
    af_code: card.code,
  })
}

const totalPages = computed(() => feedbackPages.value.length)
const safeTrackPage = computed(() => {
  if (totalPages.value <= 1) {
    return 0
  }

  return Math.min(Math.max(trackPage.value, 0), totalPages.value + 1)
})

let autoplayTimer: ReturnType<typeof setInterval> | undefined
let tabletMediaQuery: MediaQueryList | null = null
let transitionRestoreId = 0

const enableTrackTransitionAfterLayout = async () => {
  const restoreId = ++transitionRestoreId

  if (!import.meta.client) {
    isTrackTransitionEnabled.value = true
    return
  }

  await nextTick()
  void feedbackTrackRef.value?.offsetWidth

  requestAnimationFrame(() => {
    if (restoreId !== transitionRestoreId) {
      return
    }

    isTrackTransitionEnabled.value = true
  })
}

const syncTabletLayout = () => {
  if (!import.meta.client) {
    return
  }

  isTabletLayout.value = window.innerWidth <= 1200
}

const goNextPage = () => {
  if (totalPages.value <= 1) {
    return
  }

  isTrackTransitionEnabled.value = true
  trackPage.value += 1
}

/**
 * 真实页前后各补一个克隆页，切到克隆页后关闭过渡并瞬间校正，形成无缝左滑循环。
 */
const resetCarouselTrack = () => {
  isTrackTransitionEnabled.value = false
  trackPage.value = totalPages.value > 1 ? 1 : 0

  void enableTrackTransitionAfterLayout()
}

const onTrackTransitionEnd = () => {
  if (totalPages.value <= 1) {
    return
  }

  if (trackPage.value === totalPages.value + 1) {
    isTrackTransitionEnabled.value = false
    trackPage.value = 1
  } else if (trackPage.value === 0) {
    isTrackTransitionEnabled.value = false
    trackPage.value = totalPages.value
  } else {
    return
  }

  void enableTrackTransitionAfterLayout()
}

const stopAutoplay = () => {
  if (!autoplayTimer) {
    return
  }

  clearInterval(autoplayTimer)
  autoplayTimer = undefined
}

const startAutoplay = () => {
  stopAutoplay()

  if (isFeedbackLoading.value || totalPages.value <= 1) {
    return
  }

  autoplayTimer = setInterval(() => {
    goNextPage()
  }, AUTOPLAY_INTERVAL)
}

watch(totalPages, () => {
  resetCarouselTrack()
  startAutoplay()
})

watch(isTabletLayout, () => {
  resetCarouselTrack()
})

watch(isFeedbackLoading, (loading) => {
  if (loading) {
    stopAutoplay()
    return
  }

  startAutoplay()
})

watch(
  tabs,
  (nextTabs) => {
    const selectedType =
      typeof feedbackPayload.value.selectedTagType === 'number'
        ? feedbackPayload.value.selectedTagType
        : activeTabType.value
    const selectedByTypeIndex = selectedType !== undefined ? nextTabs.findIndex((tab) => tab.type === selectedType) : -1
    const selectedByLabelIndex = nextTabs.findIndex((tab) => tab.label === activeTab.value)
    const nextActiveTabIndex = selectedByTypeIndex >= 0 ? selectedByTypeIndex : Math.max(selectedByLabelIndex, 0)
    const nextActiveTab = nextTabs[nextActiveTabIndex]

    activeTabIndex.value = nextActiveTabIndex
    activeTab.value = nextActiveTab?.label || ''
    activeTabType.value = nextActiveTab?.type
  },
  { immediate: true },
)

onMounted(() => {
  if (import.meta.client) {
    tabletMediaQuery = window.matchMedia('(max-width: 1200px)')
    syncTabletLayout()
    tabletMediaQuery.addEventListener('change', syncTabletLayout)
  }

  nextTick(updateTabMask)
  tabsRef.value?.addEventListener('scroll', updateTabMask, { passive: true })
  window.addEventListener('resize', updateTabMask)

  startAutoplay()
})

onBeforeUnmount(() => {
  tabsRef.value?.removeEventListener('scroll', updateTabMask)
  window.removeEventListener('resize', updateTabMask)
  tabletMediaQuery?.removeEventListener('change', syncTabletLayout)
  stopAutoplay()
})
</script>

<template>
  <section v-if="tabs.length > 0 || feedbackItems.length > 0" class="feedback" aria-label="Feedback">
    <h2 class="feedback-title flex items-center justify-between">
      <span>{{ t('82430') }}</span>
      <NuxtLink
        :to="localePath(feedbackMoreRoute)"
        target="_blank"
        class="action-btn flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-[4px] rtl:rotate-180 hover:[&_.action-icon_path]:!fill-[#292A2E]"
        aria-label="View feedback reviews"
        @click="matomo.trackEvent('af_Exposure_List', { af_source: '首页' })"
      >
        <ArrowRight class="action-icon" aria-hidden="true" />
      </NuxtLink>
    </h2>

    <div v-if="tabs.length > 0" class="feedback-toolbar">
      <div class="feedback-tabs-wrap">
        <div ref="tabsRef" class="feedback-tabs" role="tablist" aria-label="Feedback categories">
          <button
            v-for="(tab, index) in tabs"
            :key="tab.label"
            :ref="(el) => setTabButtonRef(el, index)"
            type="button"
            class="feedback-tab"
            :class="{ 'is-active': tab.label === activeTab }"
            role="tab"
            :aria-selected="tab.label === activeTab"
            :aria-disabled="isFeedbackLoading && tab.label === activeTab"
            @click="onTabClick(tab, index)"
          >
            {{ tab.label }}
          </button>
        </div>
        <div v-show="showRightMask" class="feedback-tabs-mask" aria-hidden="true"></div>
      </div>
    </div>

    <div class="feedback-cards" :aria-busy="isFeedbackLoading" @mouseenter="stopAutoplay" @mouseleave="startAutoplay">
      <div
        v-if="isFeedbackLoading"
        class="feedback-skeleton-page"
        role="status"
        aria-live="polite"
        aria-label="Loading feedback"
      >
        <div
          v-for="(row, rowIndex) in skeletonRows"
          :key="`skeleton-row-${rowIndex}`"
          class="feedback-row feedback-skeleton-row"
          :class="{ 'with-top-line': rowIndex > 0 }"
        >
          <div
            v-for="item in row"
            :key="`skeleton-item-${item}`"
            class="feedback-item feedback-skeleton-item"
            aria-hidden="true"
          >
            <div class="item-header">
              <span class="feedback-skeleton-block item-logo"></span>
              <span class="feedback-skeleton-block skeleton-company"></span>
            </div>

            <div class="item-content-card">
              <span class="feedback-skeleton-block skeleton-content-line"></span>
              <span class="feedback-skeleton-block skeleton-content-line is-medium"></span>
              <span class="feedback-skeleton-block skeleton-content-line is-short"></span>
              <div class="item-meta-row">
                <div class="item-author">
                  <span class="feedback-skeleton-block item-avatar"></span>
                  <span class="feedback-skeleton-block skeleton-author"></span>
                </div>
                <span class="feedback-skeleton-block skeleton-meta"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        ref="feedbackTrackRef"
        class="feedback-track"
        :class="{ 'without-transition': !isTrackTransitionEnabled }"
        :style="{ transform: `translateX(-${safeTrackPage * 100}%)` }"
        @transitionend.self="onTrackTransitionEnd"
      >
        <div v-for="(page, pageIndex) in carouselPageRows" :key="pageIndex" class="feedback-page">
          <div
            v-for="(row, rowIndex) in page"
            :key="`${pageIndex}-${rowIndex}`"
            class="feedback-row"
            :class="{ 'with-top-line': rowIndex > 0 }"
          >
            <NuxtLink
              v-for="card in row"
              :key="card.id"
              :to="localePath(`/reviews/${card.reviewId}`)"
              target="_blank"
              class="feedback-item cursor-pointer hover:[&_.item-content-card]:!bg-[#F0F1F2]"
              @click="onFeedbackCardClick(card)"
            >
              <div class="item-header">
                <CommonImage
                  :src="card.logo"
                  :alt="`${card.company} logo`"
                  class="item-logo"
                  loading="lazy"
                  object-fit="cover"
                />
                <h3 class="item-company">{{ card.company }}</h3>
              </div>

              <div class="item-content-card">
                <p class="item-content">{{ card.content }}</p>
                <div class="item-meta-row">
                  <div class="item-author">
                    <CommonImage
                      :src="card.avatar"
                      :alt="`${card.author} avatar`"
                      class="item-avatar"
                      loading="lazy"
                      object-fit="cover"
                    />
                    <span class="item-author-name">{{ card.author }}</span>
                  </div>
                  <span v-if="card.country || card.timeText" class="item-meta-text">
                    <span v-if="card.country" class="item-country">{{ card.country }}</span>
                    <span v-if="card.country && card.timeText" class="item-meta-separator"> - </span>
                    <span v-if="card.timeText" dir="ltr" class="item-time">{{ card.timeText }}</span>
                  </span>
                </div>
              </div>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="less">
.feedback {
  margin: 0 auto;
  max-width: 1200px;
  background: #fff;
  padding: 80px 0;
  display: flex;
  flex-direction: column;
  gap: 20px;

  .feedback-title {
    margin: 0;
    color: #292a2e;
    font-family: Outfit, sans-serif;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
  }

  .feedback-toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0;

    .feedback-tabs-wrap {
      position: relative;
      width: 1100px;
      max-width: calc(100% - 100px);
      min-width: 0;
      flex: 0 1 auto;
    }

    .feedback-tabs {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      min-width: 0;
      overflow-x: auto;
      overflow-y: hidden;
      padding: 12px 0;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }

      .feedback-tab {
        flex-shrink: 0;
        box-sizing: border-box;
        border-radius: 2px;
        border: 0.5px solid rgba(11, 18, 40, 0.14);
        background: #fff;
        color: #292a2e;
        padding: 8px 12px;
        font-family: Outfit, sans-serif;
        font-size: 13px;
        font-weight: 400;
        line-height: 20px;
        cursor: pointer;

        &.is-active {
          border-color: #112d73;
          background: #112d73;
          color: #fff;
          font-weight: 500;
        }

        &[aria-disabled='true'] {
          cursor: default;
        }
      }
    }

    .feedback-tabs-mask {
      position: absolute;
      top: 0;
      right: 0;
      width: 32px;
      height: 60px;
      pointer-events: none;
      background: linear-gradient(270deg, #fff 25%, rgba(255, 255, 255, 0) 100%);
    }

    .feedback-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 20px;
      width: 100px;
      flex: 0 0 100px;

      .action-btn {
        width: 40px;
        height: 40px;
        border: none;
        border-radius: 12px;
        background: #f8f8f8;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 10px;

        .action-icon {
          width: 20px;
          height: 20px;
        }

        &.is-disabled {
          cursor: default;
        }
      }
    }
  }

  .feedback-cards {
    width: 100%;
    overflow: hidden;

    .feedback-track {
      display: flex;
      direction: ltr;
      transition: transform 0.6s ease;
      will-change: transform;

      &.without-transition {
        transition: none;
      }
    }

    .feedback-page {
      width: 100%;
      flex: 0 0 100%;
    }

    .feedback-skeleton-page {
      width: 100%;
    }

    :global(html[dir='rtl']) .feedback-page {
      direction: rtl;
    }

    .feedback-row {
      position: relative;
      display: grid;
      grid-template-columns: minmax(0, 1fr) 80px minmax(0, 1fr) 80px minmax(0, 1fr);

      &::before,
      &::after {
        content: '';
        position: absolute;
        top: 0;
        height: 100%;
        width: 1px;
        background: rgba(38, 38, 38, 0.1);
      }

      &::before {
        left: calc((100% - 160px) / 3 + 40px);
      }

      &::after {
        left: calc(((100% - 160px) / 3) * 2 + 120px);
      }

      &.with-top-line {
        border-top: 1px solid rgba(38, 38, 38, 0.1);
      }

      .feedback-item {
        min-width: 0;
        padding: 40px 0;
        display: flex;
        flex-direction: column;
        gap: 40px;
        z-index: 1;

        &:nth-child(1) {
          grid-column: 1;
        }

        &:nth-child(2) {
          grid-column: 3;
        }

        &:nth-child(3) {
          grid-column: 5;
        }

        .item-header {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 12px;

          .item-logo {
            width: 60px;
            height: 60px;
            border-radius: 8px;
            flex-shrink: 0;
          }

          .item-company {
            margin: 0;
            color: #292a2e;
            font-family: Outfit, sans-serif;
            font-size: 20px;
            font-weight: 600;
            line-height: 28px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .item-content-card {
          width: 100%;
          border-radius: 12px;
          background: #f8f8f8;
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 20px 30px;
          display: flex;
          flex-direction: column;
          gap: 20px;

          .item-content {
            margin: 0;
            height: 88px;
            overflow: hidden;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 4;
            color: #505258;
            font-family: Outfit, sans-serif;
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
          }

          .item-meta-row {
            min-width: 0;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;

            .item-author {
              flex: 1 1 auto;
              min-width: 0;
              display: flex;
              align-items: center;
              gap: 8px;

              .item-avatar {
                width: 22px;
                height: 22px;
                border-radius: 50%;
                flex-shrink: 0;
              }

              .item-author-name {
                color: #8c8f97;
                font-family: Outfit, sans-serif;
                font-size: 14px;
                font-weight: 400;
                line-height: 22px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }

            .item-meta-text {
              flex: 0 0 auto;
              color: #8c8f97;
              text-align: right;
              font-family: Outfit, sans-serif;
              font-size: 12px;
              font-weight: 400;
              line-height: 18px;
              white-space: nowrap;
            }
          }
        }

        &.feedback-skeleton-item {
          pointer-events: none;

          .feedback-skeleton-block {
            display: block;
            overflow: hidden;
            position: relative;
            background: #f0f1f2;

            &::after {
              content: '';
              position: absolute;
              top: 0;
              left: -120%;
              width: 120%;
              height: 100%;
              background: linear-gradient(90deg, rgba(240, 241, 242, 0) 0%, #fafafa 50%, rgba(240, 241, 242, 0) 100%);
              animation: feedbackSkeletonShimmer 1.2s ease-in-out infinite;
            }
          }

          .skeleton-company {
            width: 160px;
            height: 24px;
            border-radius: 4px;
          }

          .skeleton-content-line {
            width: 100%;
            height: 16px;
            border-radius: 4px;

            &.is-medium {
              width: 84%;
            }

            &.is-short {
              width: 62%;
            }
          }

          .skeleton-author {
            width: 88px;
            height: 18px;
            border-radius: 4px;
          }

          .skeleton-meta {
            width: 72px;
            height: 18px;
            border-radius: 4px;
            flex: 0 0 auto;
          }
        }
      }
    }
  }
}

@keyframes feedbackSkeletonShimmer {
  100% {
    left: 120%;
  }
}

@media screen and (max-width: 1200px) {
  .feedback {
    width: calc(100% - 48px);

    .feedback-toolbar {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;

      .feedback-tabs-wrap {
        width: 100%;
        max-width: 100%;
      }

      .feedback-tabs {
        width: 100%;
        max-width: 100%;
      }

      .feedback-actions {
        align-self: flex-end;
      }
    }

    .feedback-cards {
      overflow: hidden;

      .feedback-track {
        width: 100%;
      }

      .feedback-page {
        min-width: 0;
      }

      .feedback-row {
        grid-template-columns: minmax(0, 1fr) 48px minmax(0, 1fr);

        &::before {
          left: calc((100% - 48px) / 2 + 24px);
        }

        &::after {
          display: none;
        }

        .feedback-item {
          &:nth-child(1) {
            grid-column: 1;
          }

          &:nth-child(2) {
            grid-column: 3;
          }
        }
      }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .feedback {
    .feedback-cards {
      .feedback-row {
        .feedback-item.feedback-skeleton-item {
          .feedback-skeleton-block::after {
            animation: none;
          }
        }
      }
    }
  }
}
</style>
